import React, { useRef, useState } from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';

import { useOnClickOutside, useOnScroll } from '@flywire/react-hooks';
import classNames from 'classnames';

import { trackCheckMarkup } from 'actions';
import {
  EXCHANGE_RATE_DECIMAL_POSITIONS,
  MARKUP_RATE_DECIMAL_POSITIONS,
} from 'constants/index';

import { useOfferContext } from '../../Offer';
import { ReactComponent as InfoIcon } from './ico-info.svg';

import './FXInfo.scss';

type FXInfoProps = ConnectedProps<typeof connector> & {
  exchangeRate: number;
  markupRate: number;
  onToggle?: () => void;
};

const FXInfoComponent = ({
  exchangeRate,
  markupRate,
  onToggle,
}: FXInfoProps) => {
  const [shouldShowTooltip, setShouldShowTooltip] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const closeTooltip = () => setShouldShowTooltip(false);
  const { i18n } = useOfferContext();

  useOnClickOutside(ref, closeTooltip);
  useOnScroll(closeTooltip);

  const toggleTooltip = () => {
    if (!shouldShowTooltip) {
      onToggle?.();
    }

    setShouldShowTooltip(!shouldShowTooltip);
  };

  const roundedMarkupRate =
    markupRate && markupRate.toFixed(MARKUP_RATE_DECIMAL_POSITIONS);
  const roundedExchangeRate =
    exchangeRate && exchangeRate.toFixed(EXCHANGE_RATE_DECIMAL_POSITIONS);

  return (
    <>
      <div ref={ref} className="Offer-FXInfo">
        <button
          className="Offer-FXInfo-button"
          onClick={toggleTooltip}
          aria-labelledby="fxInfo-description"
        >
          <InfoIcon className="Offer-FXInfo-icon" />
          <span>{i18n.t('paymentOptions.fxInfo.text')}</span>
        </button>
        <div
          id="fxInfo-description"
          className={classNames('Offer-FXInfo-tooltip', {
            'Offer-FXInfo-tooltip--visible': shouldShowTooltip,
          })}
          role="tooltip"
          data-testid="fx-content"
        >
          <ul>
            <li data-testid="markupRate">
              {`${i18n.t(
                'paymentOptions.fxInfo.markupRate',
              )}: ${roundedMarkupRate} %`}
            </li>
            <li data-testid="exchangeRate">
              {`${i18n.t(
                'paymentOptions.fxInfo.exchangeRate',
              )}: ${roundedExchangeRate}`}
            </li>
          </ul>
        </div>
      </div>
    </>
  );
};

const mapDispatchToProps = {
  trackCheckMarkup,
};

const connector = connect(null, mapDispatchToProps);
const FXInfo = connector(FXInfoComponent);

export { FXInfo };
